import { useContext, useRef, useState } from 'react';
import Button from 'components/button';
import Icon from 'components/icons';
import Modal from 'components/modal';
import Notification from 'components/notification';
import {
  HeaderContainer,
  Input,
  ModalActions,
  ModalContainer,
  ModalContent,
  ReportingTitle as ModalTitle,
} from 'components/modal/styles';
import { colors } from 'constants/styles/colors';
import { screenMd } from 'constants/styles/breakpoints';
import { SessionContext } from 'contexts/session';
import { ReportsContext } from 'contexts/reporting';
import { postReportScheduleAction } from 'contexts/reporting/actions';
import { PopularReportsContext } from 'contexts/reporting';
import { format } from 'date-fns';
import { isMobile } from 'react-device-detect';
import styled from 'styled-components';
import {
  getFirstBusinessDayOfNextMonth,
  getFirstBusinessDayOfNextQuarter,
  getNextMonday,
} from 'utils/helpers';
import Checkbox from 'components/checkbox';

interface ScheduleReportModalProps {
  filtersType: string;
  closeModal: () => void;
  isPopularReport?: boolean;
  reportDefaultName?: string;
  reportId?: string;
  currentFrequencies?: string[];
}

const formatDate = (date: Date) => {
  return format(date, "MMMM d, yyyy '- (UTC)'");
};

const nextMonday = formatDate(getNextMonday());
const firstBusinessDayOfNextMonth = formatDate(getFirstBusinessDayOfNextMonth());
const firstBusinessDayOfNextQuarter = formatDate(getFirstBusinessDayOfNextQuarter());

const frequencyOptions = [
  {
    label: 'Weekly',
    description: 'Sent Mondays · Covers previous week',
    next_execution_date: nextMonday,
    value: 'weekly',
  },
  {
    label: 'Bi-weekly',
    description: 'Sent every other Monday · Covers previous 2 weeks',
    next_execution_date: nextMonday,
    value: 'biweekly',
  },
  {
    label: 'Monthly',
    description: 'Sent on first business day of month · Covers previous month',
    next_execution_date: firstBusinessDayOfNextMonth,
    value: 'monthly',
  },
  {
    label: 'Quarterly',
    description: 'Sent on first business day of quarter · Covers previous quarter',
    next_execution_date: firstBusinessDayOfNextQuarter,
    value: 'quarterly',
  },
];

const buildReportName = (accountName: string, filtersType: string) => {
  return `${accountName.trim().replace(/\s+/g, '-').toLowerCase()}_${filtersType}`;
};

const FrequencyOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;

  p {
    margin-bottom: 5px;
    white-space: normal;
    max-width: 300px;
  }

  ${screenMd} {
    p {
      max-width: 425px;
    }
  }

  .next-execution {
    color: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyDarker};
    font-size: 12px;
    font-style: normal;
    line-height: 14px;
  }
  && .ant-checkbox-wrapper > span:not(.ant-checkbox) {
    display: none !important;
  }
  && .ant-checkbox-disabled {
    opacity: 0.5;
  }
`;

const FrequencyDescription = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionLabel = styled.span`
  color: var(--black-darkr, #1a2535);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const OptionDetail = styled(OptionLabel)`
  font-weight: 400;
`;

const FrequencyOptionsContainer = styled.div`
  border-radius: 10px;
  max-width: 500px;

  display: flex;
  gap: 12px;
  flex-direction: column;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  & * {
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;

    color: ${({ theme }) => theme.default.colors.palette.Business_LightThemeGreyDarker};
  }
`;

const Label = styled.div`
  color: var(--black-darkr, #1a2535);

  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  ${screenMd} {
    font-weight: 700;
  }
`;

const Content = styled(ModalContent)`
  gap: 15px;
  display: flex;
  flex-direction: column;

  padding: 13px 15px 0 15px;

  ${screenMd} {
    padding: 30px;
  }
`;

const ScheduleReportModal = ({
  filtersType,
  closeModal,
  isPopularReport = false,
  reportDefaultName = null,
  reportId = null,
  currentFrequencies = [],
}: ScheduleReportModalProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { activeFilters } = useContext(ReportsContext);
  const { account } = useContext(SessionContext);

  const intializeFrecuencies = () => {
    if (currentFrequencies?.length > 0) {
      return frequencyOptions.filter((option) => currentFrequencies.includes(option.value));
    }
    return [];
  };

  const [frequencyList, setFrequencyList] = useState(intializeFrecuencies());
  const [disabledFrequencies] = useState(() => [...frequencyList]);
  const [reportName, setReportName] = useState(
    reportDefaultName || buildReportName(account?.name || '', filtersType)
  );
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { schedulePopularReport, updatingPopularReport } = useContext(PopularReportsContext);
  const [isScheduling, setIsScheduling] = useState(false);

  const handleInputFocus = () => {
    inputRef?.current?.focus();
  };

  const handleFrequencyChange = (key: string) => {
    const selectedFrequency = frequencyOptions.find((option) => option.value === key);
    if (!selectedFrequency) return;
    frequencyList.some((f) => f.value === selectedFrequency.value)
      ? setFrequencyList(frequencyList.filter((f) => f.value !== selectedFrequency.value))
      : setFrequencyList([...frequencyList, selectedFrequency]);
  };

  const handleReportNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setReportName(e.currentTarget.value);
  };

  const saveScheduleReport = async () => {
    const frequencies = frequencyList
      .filter((f) => disabledFrequencies.indexOf(f) === -1)
      .map((f) => f.value);
    if (isPopularReport) {
      await schedulePopularReport(frequencies, reportId);
      closeModal();
    } else {
      try {
        const payload = {
          name: reportName,
          frequency: frequencies,
          filters: {
            columns: activeFilters,
            type: filtersType,
          },
        };
        setIsScheduling(true);
        await postReportScheduleAction(payload);
        Notification({
          text: 'Report scheduled! Edit preferences in “Manage my scheduled reports”',
          type: 'success',
          allowWidthGrow: true,
        });
      } catch (error) {
        Notification({
          text: 'Something went wrong while saving your changes. Please try again.',
          type: 'error',
        });
      } finally {
        setIsScheduling(false);
        closeModal();
      }
    }
  };

  return (
    <Modal
      style={isMobile ? {} : { top: '-10vh' }}
      fullscreen={isMobile}
      visible
      footer={null}
      closeIconColor="#fff"
      onCancel={closeModal}
    >
      <ModalContainer>
        <HeaderContainer isReporting>
          <ModalTitle>Schedule Report Delivery</ModalTitle>
        </HeaderContainer>
        <Content>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            {isPopularReport ? (
              <Label>
                <span style={{ fontWeight: 400 }}>Schedule automatic delivery of </span>
                {reportName}.
              </Label>
            ) : (
              <>
                <Label>Report name</Label>
                <InputWrapper>
                  <Input
                    ref={inputRef}
                    type="text"
                    value={reportName}
                    onChange={handleReportNameChange}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                  />
                  {!isInputFocused && (
                    <Icon
                      size="small"
                      icon="Edit2"
                      style={{
                        position: 'absolute',
                        right: '10px',
                        bottom: '14px',
                        cursor: 'pointer',
                        color: '#999',
                      }}
                      onClick={handleInputFocus}
                    />
                  )}
                </InputWrapper>
              </>
            )}
          </div>
          <div>
            <Label style={{ padding: '10px 0', fontWeight: 700 }}>Delivery Frequency</Label>
            <FrequencyOptionsContainer>
              {frequencyOptions.map((option, index) => (
                <>
                  <FrequencyOption
                    key={option.value}
                    onClick={() => {
                      if (disabledFrequencies.some((f) => f.value === option.value)) {
                        return;
                      }
                      handleFrequencyChange(option.value);
                    }}
                  >
                    <Checkbox
                      controlled
                      checked={frequencyList.some((f) => f.value === option.value)}
                      disabled={disabledFrequencies.some((f) => f.value === option.value)}
                    />
                    <FrequencyDescription>
                      <p>
                        <OptionLabel>{option.label}: </OptionLabel>
                        <OptionDetail>{option.description}</OptionDetail>
                      </p>
                      <div className="next-execution">Next: {option.next_execution_date}</div>
                    </FrequencyDescription>
                  </FrequencyOption>
                  {index !== frequencyOptions.length - 1 && (
                    <div
                      style={{
                        width: '100%',
                        height: '1px',
                        backgroundColor: colors.palette.Business_LightThemeGreyLighter,
                      }}
                    />
                  )}
                </>
              ))}
            </FrequencyOptionsContainer>
          </div>
        </Content>
        <ModalActions>
          <Button $terciary onClick={closeModal}>
            Cancel
          </Button>
          <Button
            disabled={
              !frequencyList.some((f) => disabledFrequencies.indexOf(f) === -1) ||
              (isPopularReport && !reportId) ||
              isScheduling ||
              updatingPopularReport
            }
            $primary
            onClick={saveScheduleReport}
          >
            Save and schedule
          </Button>
        </ModalActions>
      </ModalContainer>
    </Modal>
  );
};

export default ScheduleReportModal;
